import React, { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { criarUsuarioEAtleta } from '../services/userService';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { validateEmail } from '../utils/email';
import { validatePassword } from '../utils/password';
import { validateCpf } from '../utils/cpf';

const registerSchema = z
  .object({
    name: z.string().nonempty('Nome é obrigatório'),
    cpf: z
      .string()
      .min(1, 'CPF é obrigatório')
      .refine((value) => validateCpf(value), {
        message: 'CPF inválido',
      }),
    email: z
      .string()
      .min(1, 'Email é obrigatório')
      .refine((value) => validateEmail(value), {
        message: 'Email inválido',
      }),
    password: z
      .string()
      .min(1, 'Senha é obrigatória')
      .refine((value) => validatePassword(value), {
        message: 'A senha deve ter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma letra minúscula, um número e um caractere especial (@$!%*?&.).',
      }),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
    terms: z
      .boolean()
      .refine((val) => val, {
        message: 'É necessário aceitar os Termos e Condições',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem.',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

const Register: React.FC = () => {
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      cpf: '',
      email: '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
  });

  const termosChecked = watch('terms', false);

  useApiErrorHandler((message) => {
    setFormError(`Falha ao registrar usuário: ${message}`);
    return true;
  });

  const formatCpfInput = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 11);

    const part1 = digitsOnly.slice(0, 3);
    const part2 = digitsOnly.slice(3, 6);
    const part3 = digitsOnly.slice(6, 9);
    const part4 = digitsOnly.slice(9, 11);

    let formatted = part1;
    if (part2) {
      formatted = `${formatted ? `${formatted}.` : ''}${part2}`;
    }
    if (part3) {
      formatted = `${formatted}.${part3}`;
    }
    if (part4) {
      formatted = `${formatted}-${part4}`;
    }

    return formatted;
  };

  const onSubmit = async (data: RegisterFormData) => {
    setFormError('');
    try {
      const cpfDigits = data.cpf.replace(/\D/g, '');
      await criarUsuarioEAtleta(data.name, data.email, data.password, cpfDigits);
      navigate('/home');
    } catch (err) {
      // Error is handled globally via the ApiErrorProvider
    }
  };

  return (
    <Fragment>
      <div className="main-wrap">
              <div className="nav-header bg-transparent shadow-none border-0">
                <div className="nav-top w-100">
                  <Link to="/" className='ms-5 mt-5'>
                    <img src="/assets/images/logo-inplay.png" alt="InPlay Logo" className="nav-logo" style={{ height: 60 }} />
                  </Link>
                  <button className="nav-menu me-0 ms-auto"></button>
                  <Link to="/login" className="header-btn d-none d-lg-block bg-dark fw-500 text-white font-xsss p-3 ms-auto w100 text-center lh-20 rounded-xl">Login</Link>
                  <Link to="/register" className="header-btn d-none d-lg-block bg-current fw-500 text-white font-xsss p-3 ms-2 w100 text-center lh-20 rounded-xl">Cadastrar</Link>
                </div>
              </div>
              <div className="row">
                <div
                  className="col-xl-5 d-none d-xl-block p-0 vh-100 bg-image-contain bg-no-repeat bg-primary"
                  style={{
                    backgroundImage: `url("assets/images/login-banner.png")`,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain'
                  }}
                ></div>
                <div className="col-xl-7 vh-100 align-items-center d-flex bg-white rounded-3 overflow-hidden">
                  <div className="card shadow-none border-0 ms-auto me-auto login-card">
                    <div className="card-body rounded-0 text-left">
                <h2 className="fw-700 display1-size display2-md-size mb-4">Crie uma conta</h2>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {formError && (
                    <p className="text-danger mb-3">{formError}</p>
                  )}
                  <div className="form-group icon-input mb-3">
                    <i className="font-sm ti-user text-grey-500 pe-0"></i>
                    <input
                      type="text"
                      className="style2-input ps-5 form-control text-grey-900 font-xsss fw-600"
                      placeholder="Nome Completo"
                      {...register('name')}
                    />
                    {errors.name && (
                      <p className="text-danger mb-0">{errors.name.message}</p>
                    )}
                  </div>
                  <div className="form-group icon-input mb-3">
                    <i className="font-sm ti-id-badge text-grey-500 pe-0"></i>
                    <Controller
                      name="cpf"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          inputMode="numeric"
                          className="style2-input ps-5 form-control text-grey-900 font-xsss fw-600"
                          placeholder="CPF"
                          value={formatCpfInput(field.value ?? '')}
                          onChange={(event) => field.onChange(event.target.value.replace(/\D/g, '').slice(0, 11))}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                    {errors.cpf && (
                      <p className="text-danger mb-0">{errors.cpf.message}</p>
                    )}
                  </div>
                  <div className="form-group icon-input mb-3">
                    <i className="font-sm ti-email text-grey-500 pe-0"></i>
                    <input
                      type="text"
                      className="style2-input ps-5 form-control text-grey-900 font-xsss fw-600"
                      placeholder="Endereço de Email"
                      {...register('email')}
                    />
                    {errors.email && (
                      <p className="text-danger mb-0">{errors.email.message}</p>
                    )}
                  </div>
                  <div className="form-group icon-input mb-3">
                    <input
                      type="password"
                      className="style2-input ps-5 form-control text-grey-900 font-xss ls-3"
                      placeholder="Senha"
                      {...register('password')}
                    />
                    <i className="font-sm ti-lock text-grey-500 pe-0"></i>
                    {errors.password && (
                      <p className="text-danger mb-0">{errors.password.message}</p>
                    )}
                  </div>
                  <div className="form-group icon-input mb-3">
                    <input
                      type="password"
                      className="style2-input ps-5 form-control text-grey-900 font-xss ls-3"
                      placeholder="Confirme a Senha"
                      {...register('confirmPassword')}
                    />
                    <i className="font-sm ti-lock text-grey-500 pe-0"></i>
                    {errors.confirmPassword && (
                      <p className="text-danger mb-0">{errors.confirmPassword.message}</p>
                    )}
                  </div>
                  <div className="form-check text-left mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input mt-2"
                      id="exampleCheck2"
                      {...register('terms')}
                    />
                    <label className="form-check-label font-xsss text-grey-500">
                      Aceito os Termos e Condições
                    </label>
                    {errors.terms && (
                      <p className="text-danger mb-0">{errors.terms.message}</p>
                    )}
                  </div>
                  <div className="form-group mb-1">
                    <button
                      type="submit"
                      className="btn form-control btn-primary text-center style2-input text-white fw-600 border-0 p-0 fs-5"
                      disabled={!termosChecked}
                    >
                      Registrar
                    </button>
                  </div>
                </form>
                <div className="col-sm-12 p-0 text-left">
                  <h6 className="text-grey-500 font-xsss fw-500 mt-0 mb-0 lh-32">
                    Já tem uma conta? <Link to="/login" className="fw-700 ms-1">faça o login</Link>
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Register;
